import React, { useMemo } from 'react'
import GoogleMapReact, { Maps, Props as GoogleMapReactProps } from 'google-map-react'
import { Typography, Card, Box, IconButton, Menu, MenuItem, Stack } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { createMapOptions } from './MapOptions'
import { MapCoordinates } from './mapTypeDefs'
import defaultIcon from './mapIcon_none.png'
import { useTheme } from '@mui/material/styles'
import { useCustomTranslation } from '@e3dc-react/shell/src/Hooks/useCustomTranslation'
import { J2Editacontent, J2Location, J2Settings } from '@e3dc-react/icons'
import { ButtonWithLoading } from '@e3dc-react/shell/src/Elements/Buttons'
import mapIcon from './google-map.jpg'
import { MarkerType } from '@e3dc-react/shell/src/Libs/Maps/types'
import Marker from './Marker'
import { useDraggableMarker } from './useDraggableMarker'
import { BlankView, BlankViewImageSizes, BlankViewImageVariants } from '../blank-view'

const DEFAULT_LAT = 52.40133
const DEFAULT_LNG = 7.99817 //e3dc Karlstraße

interface StyleProps {
  clickable: boolean
}

const useStyles = makeStyles<StyleProps>()((theme, props) => ({
  overlay: {
    position: 'absolute',
    bottom: theme.spacing(4),
    left: '50%',
    transform: 'translateX(-50%)',
    width: 'max-content',
    maxWidth: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',

    [theme.breakpoints.up('lg')]: {
      paddingLeft: theme.spacing(8),
      paddingRight: theme.spacing(8),
    },
  },
  addressContainer: {
    padding: theme.spacing(2),
    marginRight: theme.spacing(2),
    marginLeft: theme.spacing(2),
    position: 'relative',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    minHeight: 56,
  },
  mapWrapper: {
    width: '100%',
    minHeight: 400,
    height: '100%',
    position: 'relative',
    padding: 0,
  },
  editAddressButton: {
    fontSize: 10,
    padding: 0,
    paddingLeft: theme.spacing(1),
    color: theme.palette.map.editButton,
    marginLeft: theme.spacing(5),
  },
  disabledMap: {
    border: `1px solid ${theme.palette.grey.grey06}`,
    height: '100%',
    padding: theme.spacing(4),
  },
  disabledMapImage: {
    backgroundImage: `url(${mapIcon})`,
    filter: 'blur(2px)',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  },
  disabledMapContent: {
    zIndex: 2,
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    position: 'relative',
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
  },
  mapSettings: {
    position: 'absolute',
    bottom: theme.spacing(3),
    right: theme.spacing(1),
  },
  mapContent: {
    height: '100%',
  },
  menuItem: {
    padding: 0,
    marginTop: theme.spacing(2),
  },
  installerContainer: {
    backgroundColor: theme.palette.secondary.main,
    margin: `0 ${theme.spacing(2)}`,
    padding: `${theme.spacing(2)} ${theme.spacing(4)}`,
    color: theme.palette.common.white,
  },
  addressWrapper: {
    display: 'flex',
  },
  fullAdress: {
    marginLeft: theme.spacing(1),
  },
}))

export interface DisabledMapOptions {
  text?: string
  buttonLabel?: string
  onClick?: () => void
  loading?: boolean
  disagree?: () => void
}

export interface MapProps {
  street?: string
  streetNumber?: string
  zip?: string
  city?: string
  coordinates: MapCoordinates[]
  center?: MapCoordinates
  openCookieModal: () => void
  onChildClick?: GoogleMapReactProps['onChildClick']
  elevation?: number
  showOverlay?: boolean
  defaultZoom?: number
  editable?: boolean
  onClickAdressEdit?: () => void
  disabled?: boolean
  disabledOptions?: DisabledMapOptions
  hasSettings?: boolean
  markerType?: MarkerType
  customContent?: React.ReactNode
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  icon?: any
  cornerRadius?: number
  onMarkerDragEnd?: (coords: MapCoordinates) => void
  draggableMarker?: boolean
  mapsToken: string
  children?: React.ReactNode
  dataQa?: string
}

/**
 * Map to display either users or installations
 */
const UserOrInstallationMap: React.FC<MapProps> = ({
  street,
  streetNumber,
  zip,
  city,
  children,
  coordinates,
  center,
  onChildClick,
  onClickAdressEdit,
  openCookieModal,
  disabledOptions,
  customContent,
  elevation = 1,
  showOverlay = true,
  defaultZoom = 15,
  editable = false,
  disabled = false,
  hasSettings = true,
  markerType = 'installation',
  icon,
  cornerRadius,
  draggableMarker = false,
  onMarkerDragEnd,
  mapsToken,
  dataQa,
}) => {
  const streetComplete = [street, streetNumber].join(' ')
  const cityComplete = [zip, city].join(' ')
  const theme = useTheme()
  const { classes } = useStyles({ clickable: Boolean(onChildClick) })
  const { t } = useCustomTranslation()
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const centerPoint = useMemo(() => {
    return {
      lat: center?.lat ? parseFloat(center.lat.toString()) : DEFAULT_LAT,
      lng: center?.lng ? parseFloat(center.lng.toString()) : DEFAULT_LNG,
    }
  }, [center])
  const open = Boolean(anchorEl)
  const { initDraggableMarker } = useDraggableMarker({ draggableMarker, coordinates, icon, onMarkerDragEnd })

  const handleMapSettingsClick = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget)
  }

  const handleMapSettingsClose = (): void => {
    setAnchorEl(null)
  }

  const content = (
    <>
      {disabled ? (
        <Box className={classes.disabledMap}>
          <Box className={classes.disabledMapImage} />
          <Box className={classes.disabledMapContent}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                height: '100%',
                textAlign: 'center',
                p: 2,
              }}
            >
              <BlankView
                imageVariant={BlankViewImageVariants.Worried}
                imageSize={BlankViewImageSizes.Small}
                fullHeight
                title="maps.blank-view.title"
                explanation="common.noMapPossible"
                action={{
                  text: 'common.to-cookies',
                  onClick: openCookieModal,
                }}
              />
            </Box>
          </Box>
        </Box>
      ) : (
        <Box className={classes.mapContent}>
          <GoogleMapReact
            bootstrapURLKeys={{ key: mapsToken }}
            center={centerPoint}
            defaultZoom={defaultZoom}
            options={(maps: Maps) => createMapOptions(maps, theme)}
            onChildClick={onChildClick}
            onGoogleApiLoaded={initDraggableMarker}
            yesIWantToUseGoogleMapApiInternals
          >
            {draggableMarker
              ? null
              : coordinates
                  .filter(point => point.lat && point.lng)
                  .map((point, idx) => {
                    return (
                      <Marker
                        src={icon ?? point.icon ?? defaultIcon}
                        lat={point.lat!}
                        lng={point.lng!}
                        key={point.markerKey ? `map-point-${point.markerKey}` : `map-point-${point.lat}${point.lng}${idx}`}
                        installation={point.installation}
                        clickable={Boolean(onChildClick)}
                        type={markerType}
                        cornerRadius={cornerRadius}
                      />
                    )
                  })}
          </GoogleMapReact>
          {hasSettings && (
            <IconButton
              className={classes.mapSettings}
              aria-controls={open ? 'account-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={open ? 'true' : undefined}
              onClick={handleMapSettingsClick}
            >
              <J2Settings />
            </IconButton>
          )}
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleMapSettingsClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <Typography>{t('user.googleAgreement.settings')}</Typography>
            <MenuItem className={classes.menuItem}>
              <ButtonWithLoading
                isLoading={disabledOptions?.loading}
                onClick={() => {
                  handleMapSettingsClose()
                  disabledOptions?.disagree?.()
                }}
                label={t('user.googleAgreement.disagree')}
              />
            </MenuItem>
          </Menu>
        </Box>
      )}
      {!disabled && showOverlay && (
        <Stack gap={2} className={classes.overlay}>
          <Card variant={'outlined'} className={classes.addressContainer}>
            <Box className={classes.addressWrapper}>
              <J2Location />
              <Box className={classes.fullAdress}>
                <Typography variant={'body2'} component={'div'}>
                  {streetComplete}
                </Typography>
                <Typography variant={'body2'} component={'div'}>
                  {cityComplete}
                </Typography>
              </Box>
            </Box>
            {editable && (
              <IconButton onClick={onClickAdressEdit} className={classes.editAddressButton} size="medium">
                <J2Editacontent />
              </IconButton>
            )}
          </Card>
          {children && (
            <Card variant={'outlined'} className={classes.installerContainer}>
              {children}
            </Card>
          )}
        </Stack>
      )}
      {customContent}
    </>
  )

  return (
    <Card className={classes.mapWrapper} elevation={elevation} data-qa={dataQa}>
      {content}
    </Card>
  )
}

export default UserOrInstallationMap
